/**
 * PublicDemoContext — subdomain-activated demo mode for brand / investor
 * / partner demos on `demo.trubeaute.com`.
 *
 * Distinct from `DemoModeContext.jsx`, which is a founder-only admin
 * toggle that forces TTS on during an AR-mirror pitch. THIS context
 * governs whether the WHOLE APP is running in ephemeral demo posture:
 *   • all API calls are tagged `X-Demo-Mode: 1`
 *   • sign-in surfaces are hidden
 *   • a persistent "DEMO MODE" badge with a Reset button is shown
 *   • analytics events auto-carry `demo_mode: true`
 *   • local/sessionStorage is wiped on Reset
 *
 * Activation (any one of):
 *   1. `window.location.hostname === "demo.trubeaute.com"`
 *   2. `process.env.REACT_APP_DEMO_MODE === "true"` (preview/local)
 *   3. `?demo=1` query param (one-shot for QA / preview testing)
 *
 * Session shape:
 *   • `demo_session_id` — random, per-tab, sessionStorage-backed
 *   • Reset generates a fresh id + purges every `demo_*` key across
 *     BOTH localStorage and sessionStorage, calls `/api/demo/reset`
 *     to invalidate the server-side session token, and hard-reloads
 *     to `/` so no stale React state bleeds through.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const DEMO_HOSTNAME = "demo.trubeaute.com";
const DEMO_SESSION_KEY = "demo_session_id";

// Production apex hosts — demo mode is HARD-OFF on these regardless of
// query params or env vars. Prevents casual `?demo=1` opt-in from a
// random visitor turning trubeaute.com into a demo experience.
const PRODUCTION_HOSTS = new Set(["trubeaute.com", "www.trubeaute.com"]);

const detectActive = () => {
  if (typeof window === "undefined") return false;
  const host = (window.location.hostname || "").toLowerCase();
  // Rule 1: Production apex NEVER activates demo mode. Any client-
  // controlled signal (?demo=1, env var, header) is ignored here.
  if (PRODUCTION_HOSTS.has(host)) return false;
  // Rule 2: `demo.trubeaute.com` — always ON.
  if (host === DEMO_HOSTNAME) return true;
  // Rule 3: Preview / local — env var wins.
  if (
    typeof process !== "undefined" &&
    process?.env?.REACT_APP_DEMO_MODE === "true"
  ) {
    return true;
  }
  // Rule 4: Preview / local — `?demo=1` opt-in.
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("demo") === "1") return true;
  } catch {
    /* URLSearchParams unavailable — no-op */
  }
  return false;
};

// Returns the URL demo mode should hard-reload to on Reset.
// - Production demo hostname → "/" (hostname re-activates mode)
// - Env var → "/" (backend flag re-activates mode)
// - `?demo=1` opt-in → "/?demo=1" (must preserve so mode stays on)
const _computeResetTarget = () => {
  if (typeof window === "undefined") return "/";
  const host = (window.location.hostname || "").toLowerCase();
  if (host === DEMO_HOSTNAME) return "/";
  if (
    typeof process !== "undefined" &&
    process?.env?.REACT_APP_DEMO_MODE === "true"
  ) {
    return "/";
  }
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get("demo") === "1") return "/?demo=1";
  } catch {
    /* noop */
  }
  return "/";
};

const readOrMintSessionId = () => {
  if (typeof window === "undefined") return null;
  try {
    let id = window.sessionStorage.getItem(DEMO_SESSION_KEY);
    if (!id) {
      id = `demo_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      window.sessionStorage.setItem(DEMO_SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

const wipeDemoStorage = () => {
  if (typeof window === "undefined") return;
  const wipe = (store) => {
    try {
      const keys = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (k && k.startsWith("demo_")) keys.push(k);
      }
      keys.forEach((k) => store.removeItem(k));
    } catch {
      /* storage disabled */
    }
  };
  wipe(window.localStorage);
  wipe(window.sessionStorage);
  // Also clear any auth token so the next boot mints a fresh demo user.
  try {
    window.localStorage.removeItem("session_token");
  } catch { /* noop */ }
};

// Legacy / non-demo storage keys that could route a fresh demo session
// PAST the quiz (e.g., a returning production user's hero-intent blob
// that would auto-fill the goal answer, or a saved routine that would
// pop the "Continue where you left off" nudge). Wiped on demo-mode
// activation so brand/investor demos always start at the true blank
// state — no inherited "you're already done" flags from prior real
// or preview usage of this browser.
const LEGACY_KEYS_TO_WIPE_ON_DEMO = [
  "trubeaute.heroIntent.v1",       // hero-chat pre-fill blob
  "trubeaute.abandon_toast_seen",   // "welcome back" nudge dedupe
  "trubeaute.lastRoutineId",       // last completed routine pointer
  "trubeaute.stealth_mode",        // stealth flag (would suppress analytics)
  "trubeaute.onboardingComplete",  // any legacy completion flag
  "onboarding_completed",          // defensive — matches admin metric name
  "quiz_completed",                // defensive — matches admin metric name
  "current_step",                  // defensive — hypothetical step pointer
  "selfie_taken",                  // defensive — hypothetical camera-done flag
];

const wipeLegacyDemoConflicts = () => {
  if (typeof window === "undefined") return;
  const wipe = (store) => {
    LEGACY_KEYS_TO_WIPE_ON_DEMO.forEach((k) => {
      try { store.removeItem(k); } catch { /* noop */ }
    });
  };
  wipe(window.localStorage);
  wipe(window.sessionStorage);
};

const PublicDemoContext = createContext({
  isDemoMode: false,
  demoSessionId: null,
  demoBootstrapping: false,
  setDemoBootstrapping: () => {},
  resetDemo: () => {},
});

export const PublicDemoProvider = ({ children }) => {
  const [isDemoMode] = useState(detectActive);
  const [demoSessionId, setDemoSessionId] = useState(() =>
    detectActive() ? readOrMintSessionId() : null,
  );
  // True while `PublicDemoBootstrap` is minting the initial demo user.
  // Consumed by `ProtectedRoute` so it renders the loading spinner
  // instead of redirecting to /login while the demo user is in flight
  // (the demo mint runs after AuthContext.loading has already flipped
  // to false, creating a brief `!loading && !user` window that would
  // otherwise trip the redirect).
  const [demoBootstrapping, setDemoBootstrapping] = useState(
    () => detectActive(),
  );

  // Announce demo mode to the whole app via a global flag that
  // non-React modules (like the axios interceptor in lib/api.js) can
  // read synchronously. Set as early as possible.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__trubeaute_demo_mode = isDemoMode;
    window.__trubeaute_demo_session_id = demoSessionId;
    // On first activation, evict any legacy state that could
    // short-circuit a fresh demo (e.g. a stray hero-intent blob from
    // a prior production visit that would pre-fill the quiz's goal
    // answer, or an admin-metric completion flag that would trigger
    // the "you're done" launchpad instead of the quiz). This is the
    // safety net for the founder's "the demo user should behave like
    // a brand-new user" acceptance criterion.
    if (isDemoMode) {
      wipeLegacyDemoConflicts();
    }
  }, [isDemoMode, demoSessionId]);

  const resetDemo = useCallback(async () => {
    if (typeof window === "undefined") return;
    // Best-effort server-side session invalidation + demo photo
    // purge. Doesn't block the client wipe — even if the network
    // hop fails, the local reset still runs and the TTL indexes
    // sweep every demo document within 24h (photos within 1h).
    try {
      const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      await fetch(`${backend}/api/demo/reset`, {
        method: "POST",
        credentials: "include",
        headers: {
          "X-Demo-Mode": "1",
          "Content-Type": "application/json",
          ...(typeof window !== "undefined" &&
          window.__trubeaute_demo_session_token
            ? { Authorization: `Bearer ${window.__trubeaute_demo_session_token}` }
            : {}),
        },
      });
    } catch {
      /* offline / network blip — proceed with local wipe */
    }
    wipeDemoStorage();
    // Also purge any legacy / non-demo storage keys that could route
    // the freshly-reset demo past the quiz (hero-intent pre-fill,
    // completion flags, last-routine pointer). Belt + suspenders on
    // top of the mount-time wipe.
    wipeLegacyDemoConflicts();
    // Also drop the in-memory token so a stale bearer can never
    // outlive the wipe.
    try {
      window.__trubeaute_demo_session_token = null;
      window.__trubeaute_demo_user_id = null;
    } catch { /* noop */ }
    // Hard reload via `location.replace(...)` (not `location.href = …`)
    // so the pre-reset URL is REPLACED in browser history — the back
    // button cannot restore the previous demo state. The reset target
    // is `/` on production (hostname re-activates) and `/?demo=1` on
    // preview (must preserve so mode stays on).
    window.location.replace(_computeResetTarget());
  }, []);

  const value = useMemo(
    () => ({
      isDemoMode,
      demoSessionId,
      demoBootstrapping,
      setDemoBootstrapping,
      resetDemo,
    }),
    [isDemoMode, demoSessionId, demoBootstrapping, resetDemo],
  );

  return (
    <PublicDemoContext.Provider value={value}>
      {children}
    </PublicDemoContext.Provider>
  );
};

export const usePublicDemo = () => useContext(PublicDemoContext);

// Synchronous helpers for non-React callers (axios interceptors,
// analytics helpers). Reads the same `window.__trubeaute_demo_mode`
// flag the provider stamps on mount.
export const isPublicDemoModeActive = () => {
  if (typeof window === "undefined") return false;
  return !!window.__trubeaute_demo_mode;
};

export const getPublicDemoSessionId = () => {
  if (typeof window === "undefined") return null;
  return window.__trubeaute_demo_session_id || null;
};
